import React, {useContext} from 'react';
import getConfig from 'next/config';

import Teaser from '~/src/components/teaser/Teaser';
import DebugSection from '~/src/components/debug/teaserSection/DebugSection';
import SectionContextProvider from '~/src/components/context/SectionContext';
import TeaserSectionDefault from '~/src/components/teaser/layout/default/TeaserLayoutDefault';
import TeaserSectionCarousel from '~/src/components/teaser/layout/carousel/TeaserLayoutCarousel';
import TeaserSectionEditorsPick from '~/src/components/teaser/layout/editorsPick/TeaserLayoutEditorsPick';
import TeaserLayoutNews from '~/src/components/teaser/layout/news/TeaserLayoutNews';
import TeaserLayoutVideo from '~/src/components/teaser/layout/video/TeaserLayoutVideo';
import TeaserLayoutLifestyle from '~/src/components/teaser/layout/lifestyle/TeaserLayoutLifestyle';
import TeaserLayoutRelatedContent from '~/src/components/teaser/layout/relatedContent/TeaserLayoutRelatedContent';
import TeaserLayoutPopularity from '~/src/components/teaser/layout/popularity/TeaserLayoutPopularity';

import {SitemapContext} from '~/src/components/context/SitemapContext';
import {SettingsContext} from '~/src/components/context/SettingsContext';
import {TeaserFormat, AdFormat} from '~/src/utilities/constants/formats';
import {classNames} from '~/src/utilities/helpers/css';
import {SiteArea} from '~/src/utilities/constants/navigation';
import {SectionLayout} from '~/src/utilities/constants/layout';
import {createFormat, StyleFormat} from '~/src/utilities/feed/format';
import {CustomSectionTypes} from '~/src/utilities/constants/types';
import {GetNextSideline} from '~/src/utilities/helpers/sidelineStack';
import {Logger} from '~/src/utilities/helpers/log';

import css from './TeaserSection.module.scss';

const {publicRuntimeConfig: config} = getConfig();

export interface TeaserData {
    id: string;
    type: string;
    format: StyleFormat;
    adUnitName?: string;
}

export interface SectionProps {
    title: string;
    showTitle: boolean;
    format?: StyleFormat;
    layout: string;
    carouselType?: string;
    subtype: string;
    customType?: string;
    pointerCategoryID: number;
    fullUrlPath: string;
    sortID?: number;
    elements: TeaserData[];
    trackingData?: any;
    isPlaceholder?: boolean;
    icon?: {
        variants: {
            original: {
                src: string;
            };
        };
    };
    options?: {
        // ugly but will get refactored
        [key: string]: any;
    };
    getNextSideline: GetNextSideline;
}

const getTeaserLayout = ({layout, customType}: SectionProps, siteArea: string) => {
    if (layout === SectionLayout.CAROUSEL) {
        return TeaserSectionCarousel;
    }

    if (layout === SectionLayout.EDITORIAL_PICK) {
        return TeaserSectionEditorsPick;
    }

    if (layout === SectionLayout.RELATED_CONTENT) {
        return TeaserLayoutRelatedContent;
    }

    if (customType === CustomSectionTypes.MOST_POPULAR) {
        return TeaserLayoutPopularity;
    }

    if (siteArea === SiteArea.NEWS) {
        return TeaserLayoutNews;
    }

    if (siteArea === SiteArea.VIDEO) {
        return TeaserLayoutVideo;
    }

    if (siteArea === SiteArea.LIFESTYLE) {
        return TeaserLayoutLifestyle;
    }

    return TeaserSectionDefault;
};

const getTeaserFormat = (element: TeaserData, layout: string, colorScheme?: string) => {
    if (element.type === 'ad' && !element.adUnitName) {
        return createFormat(AdFormat.INSIDE_FULL_POS);
    }

    if (layout === SectionLayout.EDITORIAL_PICK) {
        return createFormat(TeaserFormat.TEASER_TOP_LIST, TeaserFormat.TEASER_TOP_LIST, colorScheme);
    }

    return element.format;
};

const mapTeaserElements = ({elements, layout, pointerCategoryID, trackingData, options}: SectionProps, colorScheme?: string) => {
    const showLead = options?.showLead !== 'false';
    return elements.map((element, index) => {
        const teaserFormat = getTeaserFormat(element, layout, colorScheme);

        return (
            <Teaser
                key={`teaser_${index}`}
                {...element}
                pointerCategoryID={pointerCategoryID}
                format={teaserFormat}
                layout={layout}
                trackingData={trackingData}
                showLead={showLead}
            />
        );
    });
};

const TeaserSection = (props: SectionProps) => {
    const {elements, layout, carouselType, subtype, customType, pointerCategoryID} = props;

    if (!props.isPlaceholder) {
        const {getCategoryBy, all} = useContext(SitemapContext);
        const category = getCategoryBy({categoryId: pointerCategoryID});

        if (!category && layout !== SectionLayout.RELATED_CONTENT && layout !== SectionLayout.CAROUSEL && subtype !== 'custom') {
            Logger.error(`[TeaserSection] Could not find category with ID: ${pointerCategoryID}`, {props, sitemap: all});
            return config.debug.showComponents && <DebugSection {...props} />;
        }
        if (!elements.length) {
            Logger.info(`[TeaserSection] No elements for category with ID: ${pointerCategoryID}`, props);
            return config.debug.showComponents && <DebugSection {...props} />;
        }
    }

    const {siteArea} = useContext(SettingsContext);

    const colorScheme = elements.find((element) => element.format)?.format.colorScheme;
    const classes = classNames(css, 'section', layout, siteArea, carouselType, colorScheme, customType);

    const SelectedTeaserLayout = getTeaserLayout(props, siteArea);

    return (
        <SectionContextProvider value={props}>
            <section className={classes} data-testid="Section">
                <SelectedTeaserLayout {...props} colorScheme={colorScheme}>
                    {mapTeaserElements(props, colorScheme)}
                </SelectedTeaserLayout>
            </section>
        </SectionContextProvider>
    );
};

export default TeaserSection;
